import { type ComponentType, lazy } from 'react'

interface LazyRetryOptions {
  maxRetries?: number
  retryDelay?: number
}

/**
 * Checks if the error is a chunk load failure (missing file after deploy)
 * vs a transient network error that's worth retrying
 */
export function isChunkNotFoundError(error: unknown): boolean {
  if (!(error instanceof Error)) return false
  return (
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Importing a module script failed') ||
    error.message.includes('error loading dynamically imported module')
  )
}

/**
 * Enhanced lazy loading with automatic retry logic for transient network failures.
 *
 * For chunk-not-found errors (after a deploy), retrying the same URL is pointless
 * since the file no longer exists. These errors are thrown immediately so the
 * route `errorElement` (RouteErrorBoundary) can handle them with a full page reload.
 *
 * For transient network errors, retries with exponential backoff.
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  importFn: () => Promise<{ default: T }> | T,
  options: LazyRetryOptions = {},
): React.LazyExoticComponent<T> {
  const { maxRetries = 3, retryDelay = 1500 } = options

  return lazy(() => {
    return new Promise<{ default: T }>((resolve, reject) => {
      const attemptImport = async (attemptNumber: number): Promise<void> => {
        try {
          const result = await importFn()
          const normalizedResult = 'default' in result ? result : { default: result }
          resolve(normalizedResult)
        } catch (error) {
          // Chunk doesn't exist anymore (new deploy) — don't retry, let error boundary reload
          if (isChunkNotFoundError(error)) {
            console.warn('[LazyRetry] Chunk not found (likely new deploy), delegating to error boundary')
            reject(error)
            return
          }

          console.warn(
            `[LazyRetry] Import failed (attempt ${attemptNumber}/${maxRetries}):`,
            error,
          )

          if (attemptNumber < maxRetries) {
            const delay = retryDelay * Math.pow(1.5, attemptNumber - 1)
            console.log(`[LazyRetry] Retrying in ${delay}ms...`)
            setTimeout(() => attemptImport(attemptNumber + 1), delay)
          } else {
            console.error('[LazyRetry] Max retries reached, rejecting import:', error)
            reject(error)
          }
        }
      }

      attemptImport(1)
    })
  })
}

/**
 * Preload a lazy component to avoid loading delay
 * Useful for critical routes that should be loaded ahead of time
 */
export function preloadLazyComponent<T extends ComponentType<unknown>>(
  lazyComponent: React.LazyExoticComponent<T>,
): void {
  // @ts-expect-error - accessing internal _payload to trigger preload
  if (lazyComponent._payload && lazyComponent._payload._result === undefined) {
    // @ts-expect-error - accessing internal _result to trigger preload
    lazyComponent._payload._result = true
  }
}
